<script setup lang="ts">
import { updateGoogleConsent } from '~/utils/gtm'
import { isDrivePath } from '~/utils/drive-route'

const consent = useConsentStore()
const localePath = useLocalePath()
const route = useRoute()
const { t } = useI18n()

const isDriveRoute = computed(() => isDrivePath(route.path))

const ready = ref(false)
const rejectConfirmVisible = ref(false)

/**
 * How long the "you rejected cookies" notice stays on screen before it dismisses
 * itself. Dismissing is purely visual - the stored consent state is untouched - and the
 * user keeps a permanent, equally easy route back to accepting via
 * /privacy#cookie-settings, which is what GDPR Art. 7(3) requires.
 */
const REJECTED_BANNER_TIMEOUT = 10_000

/**
 * Remembering the dismissal for the tab (not in the consent store, which is persisted
 * and is only ever allowed to hold the consent decision itself) means the notice does
 * not reappear on every reload during a visit. `sessionStorage` clears when the tab
 * closes, so a genuinely new visit still gets one reminder. This component lives in
 * `app.vue` and is never remounted, so route changes cannot re-show it either.
 */
const DISMISSED_KEY = 'cookie-rejected-banner-dismissed'

const rejectedDismissed = ref(false)
let dismissTimer: ReturnType<typeof setTimeout> | null = null

function clearDismissTimer() {
  if (dismissTimer === null) return
  clearTimeout(dismissTimer)
  dismissTimer = null
}

function dismissRejectedBanner() {
  clearDismissTimer()
  rejectedDismissed.value = true
  try {
    sessionStorage.setItem(DISMISSED_KEY, '1')
  }
  catch {
    // Private mode / storage disabled - the in-memory ref still holds for this page life.
  }
}

function requestReject() {
  rejectConfirmVisible.value = true
}

function acceptCookies() {
  consent.accept()
  updateGoogleConsent(true)
}

// A *fresh* rejection should always be acknowledged on screen, even if an earlier one
// in this tab was already dismissed - hence the reset before the timer is armed.
watch(() => consent.state, (state) => {
  clearDismissTimer()

  if (state !== 'rejected') {
    rejectedDismissed.value = false
    try {
      sessionStorage.removeItem(DISMISSED_KEY)
    }
    catch { /* storage unavailable - nothing to clean up */ }
    return
  }

  if (rejectedDismissed.value) return
  dismissTimer = setTimeout(dismissRejectedBanner, REJECTED_BANNER_TIMEOUT)
})

onMounted(async () => {
  // The store may have rehydrated into `rejected` before this ran, arming a timer from
  // the watcher above with a not-yet-loaded dismissal flag. Start from a clean slate.
  clearDismissTimer()

  try {
    rejectedDismissed.value = sessionStorage.getItem(DISMISSED_KEY) === '1'
  }
  catch {
    rejectedDismissed.value = false
  }

  await nextTick()
  ready.value = true

  if (consent.state === 'rejected' && !rejectedDismissed.value) {
    dismissTimer = setTimeout(dismissRejectedBanner, REJECTED_BANNER_TIMEOUT)
  }
})

onBeforeUnmount(clearDismissTimer)
</script>

<template>
  <Teleport to="body">
    <Transition name="cookie-fade">
      <div
        v-if="ready && !isDriveRoute && consent.state === 'pending' && !rejectConfirmVisible"
        class="cookie-banner"
        role="dialog"
        aria-live="polite"
      >
        <p class="cookie-text">
          {{ t('cookies.message') }}
        </p>
        <NuxtLink :to="localePath('/privacy') + '#cookie-settings'" class="cookie-link">
          {{ t('cookies.learnMore') }}
        </NuxtLink>
        <button type="button" class="cookie-btn cookie-btn--accept" @click="acceptCookies">
          {{ t('cookies.accept') }}
        </button>
        <button type="button" class="cookie-btn cookie-btn--reject" @click="requestReject">
          {{ t('cookies.reject') }}
        </button>
      </div>
    </Transition>

    <Transition name="cookie-fade">
      <div
        v-if="ready && !isDriveRoute && consent.state === 'rejected' && !rejectedDismissed"
        class="cookie-banner cookie-banner--rejected"
        role="status"
        aria-live="polite"
      >
        <p class="cookie-text cookie-text--rejected">
          {{ t('cookies.rejectedMessage') }}
        </p>
        <div class="cookie-banner__actions">
          <button type="button" class="cookie-btn cookie-btn--reject" aria-pressed="true">
            <Icon name="tabler:check" size="1rem" />
            {{ t('cookies.reject') }}
          </button>
          <button type="button" class="cookie-btn cookie-btn--accept" @click="acceptCookies">
            {{ t('cookies.accept') }}
          </button>
        </div>
        <button
          type="button"
          class="cookie-dismiss"
          :aria-label="t('cookies.dismiss')"
          :title="t('cookies.dismiss')"
          @click="dismissRejectedBanner"
        >
          <Icon name="tabler:x" size="1rem" />
        </button>
      </div>
    </Transition>

    <CookieRejectConfirmDialog v-model:visible="rejectConfirmVisible" />
  </Teleport>
</template>

<style scoped>
.cookie-banner {
  position: fixed;
  bottom: 1.5rem;
  left: 50%;
  transform: translateX(-50%);
  width: min(560px, calc(100vw - 2rem));
  background: var(--white);
  border: 1px solid var(--ligth-blue);
  border-radius: 15px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  padding: 1.25rem 1.5rem;
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-rows: auto auto;
  column-gap: 1.25rem;
  row-gap: 0.35rem;
  align-items: center;
  z-index: var(--z-modal);
  color: var(--dark-blue);
}

.cookie-banner--rejected {
  display: flex;
  align-items: center;
  gap: 1rem;
  /* room for the absolutely positioned dismiss button */
  padding-right: 2.75rem;
}

.cookie-dismiss {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  border: none;
  border-radius: 999px;
  background: transparent;
  color: var(--dark-blue);
  cursor: pointer;
  line-height: 0;
}

.cookie-dismiss:hover {
  background: var(--ligth-blue);
}

.cookie-text {
  grid-column: 1;
  grid-row: 1;
  font-size: 13px;
  line-height: 1.5;
  margin: 0;
}

.cookie-text--rejected {
  flex: 1;
  min-width: 0;
}

.cookie-link {
  grid-column: 1;
  grid-row: 2;
  font-size: 13px;
  color: var(--dark-blue);
  font-weight: 600;
  text-decoration: underline;
}

.cookie-banner__actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
}

.cookie-btn--accept {
  grid-column: 2;
  grid-row: 1;
  align-self: center;
  background: var(--dark-blue);
  color: var(--white);
}

.cookie-btn--reject {
  grid-column: 2;
  grid-row: 2;
  justify-self: center;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  background: transparent;
  color: var(--dark-blue);
  padding: 0;
  font-weight: 600;
  text-decoration: underline;
}

.cookie-banner--rejected .cookie-btn--reject {
  grid-column: auto;
  grid-row: auto;
  justify-self: auto;
  cursor: default;
}

.cookie-btn {
  border-radius: 999px;
  border: none;
  padding: 0.55rem 1.2rem;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  font-family: Montserrat, ui-sans-serif, system-ui, sans-serif;
}

.cookie-btn--reject:hover {
  opacity: 0.8;
}

.cookie-banner--rejected .cookie-btn--reject:hover {
  opacity: 1;
}

.cookie-fade-enter-active,
.cookie-fade-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.cookie-fade-enter-from,
.cookie-fade-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(1rem);
}

@media (max-width: 560px) {
  .cookie-banner:not(.cookie-banner--rejected) {
    grid-template-columns: 1fr;
    grid-template-rows: auto auto auto auto;
  }

  .cookie-banner:not(.cookie-banner--rejected) .cookie-text {
    grid-column: 1;
    grid-row: 1;
  }

  .cookie-banner:not(.cookie-banner--rejected) .cookie-link {
    grid-column: 1;
    grid-row: 2;
  }

  .cookie-banner:not(.cookie-banner--rejected) .cookie-btn--accept {
    grid-column: 1;
    grid-row: 3;
    justify-self: start;
    margin-top: 0.5rem;
  }

  .cookie-banner:not(.cookie-banner--rejected) .cookie-btn--reject {
    grid-column: 1;
    grid-row: 4;
    justify-self: start;
  }

  .cookie-banner--rejected {
    flex-direction: column;
    align-items: flex-start;
  }

  .cookie-banner--rejected .cookie-banner__actions {
    width: 100%;
    justify-content: flex-start;
  }
}
</style>
